import { noyauNamed } from '@/lib/session-analysis';
import {
  ANALYST_LEVEL_LABELS,
  type ExpectedNoyauMark,
  type NoyauCode,
  type NoyauComparison,
  type NoyauDeterminationState,
  type NoyauExpectedProvenance,
  type NpcExpectedNoyau,
  isNoyauCode,
} from '@matter_extractor/shared';

/**
 * What the screens SAY of the expected noyau against the found one (npc.md §26.2, §26.6,
 * issue #216) - pure, in one module, so that every screen of the lot says the same words:
 * the pill « attendu » of the list of sessions (lot M) and, after it, the band of the
 * walk (lot K) and the tab of the NPC (lot L), which import from here and never reword.
 *
 * ## The verdict is said in WORDS, never by a colour alone
 *
 * « Identique », « Voisin : le Quoi tient », « Miroir : seul le Comment tient »,
 * « Aucun niveau en commun » (§26.2). The server computes the verdict (`compareNoyaux` of
 * `shared`, the only function that says it); nothing here derives one, it only words what
 * the response carries.
 */

/** The word the pill carries, and the first word of every sentence about the expected noyau. */
export const EXPECTED_WORD = 'attendu';

/**
 * Whether an expected noyau is one of the six triplets (H37): « hors base » and an absent
 * expected noyau are not, and the list marks nothing for them.
 */
export function isExpectedTriplet(
  expected: NpcExpectedNoyau | null | undefined,
): expected is NoyauCode {
  return expected !== null && expected !== undefined && isNoyauCode(expected);
}

/**
 * The expected noyau of a row, when the list is to mark it: the mark of the replay when its
 * expected noyau is a triplet, null otherwise (a real session, a replay with no NPC, an NPC
 * with no expected noyau - the keys are absent - and « hors base », H37).
 */
export function expectedTripletMark(
  mark: ExpectedNoyauMark | null | undefined,
): (ExpectedNoyauMark & { readonly expected: NoyauCode }) | null {
  if (mark === null || mark === undefined) return null;
  return isExpectedTriplet(mark.expected) ? { ...mark, expected: mark.expected } : null;
}

/** The words of the three verdicts that name no level (§26.2). */
export const VERDICT_WORDS: Readonly<Record<'same' | 'mirror' | 'none', string>> = {
  same: 'Identique',
  mirror: 'Miroir : seul le Comment tient',
  none: 'Aucun niveau en commun',
};

/**
 * The verdict in words (§26.2): « Voisin : le Quoi tient » names the level that holds, the
 * one a `near` carries in `heldLevel`. A `near` that lost its level (it never does) still
 * says « Voisin » rather than inventing one.
 */
export function verdictWords(comparison: Pick<NoyauComparison, 'verdict' | 'heldLevel'>): string {
  if (comparison.verdict !== 'near') return VERDICT_WORDS[comparison.verdict];
  return comparison.heldLevel === null
    ? 'Voisin'
    : `Voisin : le ${ANALYST_LEVEL_LABELS[comparison.heldLevel]} tient`;
}

/**
 * Why there is no found noyau, in the words of §26.3: « en attente », « sans extrait »,
 * « en échec ». A determination that rendered but holds no retained noyau (the replay stopped
 * before the first choice) says nothing more.
 */
export const NO_FOUND_STATE_WORDS: Readonly<Record<NoyauDeterminationState, string | null>> = {
  pending: 'en attente',
  'no-extracts': 'sans extrait',
  failed: 'en échec',
  rendered: null,
};

/** « Aucun noyau trouvé », and the state of the determination when it explains it. */
export function noFoundWords(state: NoyauDeterminationState | null): string {
  const why = state === null ? null : NO_FOUND_STATE_WORDS[state];
  return why === null ? 'Aucun noyau trouvé' : `Aucun noyau trouvé (${why})`;
}

/**
 * Whose expected noyau it is, and of which version: « Attendu de Nicolas Hulot v5 » when the
 * replay played that version, « Attendu actuel de Nicolas Hulot v5 » when it played none and
 * the screens took the current development version (A7). Without a name (an NPC whose name is
 * gone) it is « Attendu du NPC v5 ».
 */
export function expectedOwnerWords(
  provenance: NoyauExpectedProvenance,
  npcName: string | null | undefined,
): string {
  const lead = provenance.kind === 'current' ? 'Attendu actuel' : 'Attendu';
  const owner = npcName === null || npcName === undefined ? 'du NPC' : `de ${npcName}`;
  return `${lead} ${owner} v${provenance.versionNumber}`;
}

/**
 * The bubble of the pill « attendu » (§26.6, H37), one sentence the screens read in a breath:
 *
 *   « Attendu de Nicolas Hulot v5 : Souverain Magicien (SGP). Trouvé : Producteur Réinventeur
 *     (PSG). Aucun niveau en commun. »
 *
 * With no found noyau it says « Aucun noyau trouvé (en attente). » and no verdict. A mark whose
 * expected noyau is not a triplet has no sentence (null): the list draws no pill for it.
 */
export function expectedBubbleSentence(
  mark: ExpectedNoyauMark,
  npcName: string | null | undefined,
): string | null {
  if (!isExpectedTriplet(mark.expected)) return null;
  const parts = [`${expectedOwnerWords(mark.provenance, npcName)} : ${noyauNamed(mark.expected)}.`];
  parts.push(
    mark.found === null
      ? `${noFoundWords(mark.determinationState)}.`
      : `Trouvé : ${noyauNamed(mark.found)}.`,
  );
  if (mark.comparison !== null) parts.push(`${verdictWords(mark.comparison)}.`);
  return parts.join(' ');
}
